import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthActions } from 'state/modules/auth';
import { Box, Typography } from '@material-ui/core';
import Wizard from 'components/UI/Wizard';
import {
  CreateSaml,
  ConnectProvider,
  ImportCertificate,
} from 'components/Authentication/AuthenticationWizard';
import Button from '@material-ui/core/Button';
import { Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import i18n from 'i18n-js';
import PropTypes from 'prop-types';
import Can from 'components/Can';

const SsoTab = ({ classes }) => {
  const dispatch = useDispatch();
  const formRef = useRef(null);

  const {
    loading,
    onboarding: { exitStep, position, exitOnboarding, errors, steps, enableForm },
  } = useSelector((state) => state.auth);

  const stepsList = [
    {
      position: 1,
      key: 'create_saml',
      label: i18n.t('authentication.steps.create_saml'),
    },
    {
      position: 2,
      key: 'connect_provider',
      label: i18n.t('authentication.steps.connect_provider'),
    },
    {
      position: 3,
      key: 'certificate',
      label: i18n.t('authentication.steps.certificate'),
    },
  ];

  const handleNextPosition = () => {
    dispatch(AuthActions.changePosition('next'));
  };

  const handleNextStep = () => {
    switch (position) {
      case 0:
        if (steps?.details?.id && !enableForm) {
          return handleNextPosition();
        }
        return formRef.current.submitForm();
      case 1:
        return handleNextPosition();
      case 2:
        return formRef.current.submitForm();
      default:
        break;
    }
  };

  useEffect(() => {
    dispatch(AuthActions.fetchIdentityProvider());
  }, [dispatch]);

  useEffect(() => {
    if (exitOnboarding) {
      dispatch(AuthActions.enableIdpForm(false));
      dispatch(AuthActions.modifyPosition(0));
    }
  }, [dispatch, exitOnboarding]);

  const onSaveAndExit = () => {
    handleNextStep(true);
  };

  const onPrevious = () => {
    dispatch(AuthActions.changePosition('prev'));
  };

  const getNextStepLabel = (position, steps) => {
    if (position === 0 && steps?.details?.id) {
      return i18n.t('common.save');
    }
    if (position === 1 && !steps?.details?.has_certificate) {
      return i18n.t('authentication.wizard.import_certiticate_button');
    }
    if (position === 1 && steps?.details?.has_certificate) {
      return i18n.t('authentication.wizard.update_certificate');
    }
    return i18n.t('common.continue');
  };

  const ssoWizard = () => {
    return (
      <Box>
        <Wizard
          mode="create"
          exit={exitStep}
          steps={stepsList}
          loading={loading}
          position={position}
          nextStepLabel={getNextStepLabel(position, steps)}
          handleExit={() => {}}
          onNext={handleNextStep}
          onPrevious={onPrevious}
          onSaveAndExit={onSaveAndExit}
          customStepperClass={classes.stepperContainer}
          customRootClass={classes.customRootClass}
          displaCancelFormButton={steps?.details?.id && (position === 0 || position === 2)}
          displayContinueButton={steps?.details?.id && position === 0}
          customNextBtnClass={classes.nextButton}
          customCancelFormBtnClass={classes.nextButton}
          cancelStepLabel={position === 0 ? i18n.t('common.back') : null}
          lastStepLabel={
            position === 2 && !steps?.details?.has_certificate
              ? i18n.t('common.create')
              : i18n.t('common.confirm')
          }
          onCancelForm={() => {
            dispatch(AuthActions.enableIdpForm(false));
            dispatch(AuthActions.modifyPosition(0));
          }}
          onCotinueForm={() => {
            return handleNextPosition();
          }}
        >
          {position === 0 && (
            <CreateSaml
              formRef={formRef}
              errors={errors}
              step={steps.details}
              enableForm={enableForm}
              handleNextPosition={handleNextPosition}
            />
          )}
          {position === 1 && <ConnectProvider step={steps.details} />}
          {position === 2 && (
            <ImportCertificate formRef={formRef} errors={errors} step={steps.details} />
          )}
        </Wizard>
      </Box>
    );
  };

  const onEditForm = (edit) => {
    dispatch(AuthActions.enableIdpForm(!edit));
  };

  const shouldShowWizard = !loading && (enableForm || !steps?.details?.id);
  const shouldShowDetails = !loading && !enableForm && Boolean(steps?.details?.id);

  return (
    <Can I="create" on="sso">
      <Box
        justifyContent="left"
        alignItems="left"
        textAlign="justify"
        flexDirection="column"
        display="flex"
        className={classes.textContainer}
      >
        <Box fontSize={16} component="h5" lineHeight={1}>
          {i18n.t('authentication.sso_provider')}
        </Box>
        <Typography color="textPrimary" className={classes.description}>
          <Box fontSize={14} component="span" lineHeight={1}>
            {i18n.t('authentication.sso_provider_description')}
          </Box>
          <Box fontSize={14} mt={2} lineHeight={1} className={classes.note} component="div">
            <span
              dangerouslySetInnerHTML={{
                __html: i18n.t('authentication.sso_provider_note'),
              }}
            />
          </Box>
        </Typography>
      </Box>
      {shouldShowWizard && ssoWizard()}
      {!shouldShowWizard && shouldShowDetails && (
        <Box
          justifyContent="left"
          alignItems="left"
          textAlign="justify"
          flexDirection="column"
          display="flex"
          className={classes.textContainerDetails}
          pt={0}
        >
          <Box fontSize={16} component="h5" lineHeight={1}>
            {i18n.t('authentication.details.title')}
          </Box>
          <Typography color="textPrimary" className={classes.description}>
            <Box fontSize={14} component="span" lineHeight={1}>
              {i18n.t('authentication.details.description')}
            </Box>
          </Typography>
          <Typography color="textPrimary" component="div" align="left">
            <Box fontSize={15} component="h6" lineHeight={1} fontWeight={500} mt={2}>
              {i18n.t('authentication.wizard.fields')}
            </Box>
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={3}
            className={classes.providerInfo}
          >
            <Typography color="textPrimary" component="div" align="left">
              <Box fontSize={16} component="h6" lineHeight={1}>
                {i18n.t('authentication.wizard.idp_endpoint')}:
              </Box>
            </Typography>
            <Typography
              color="textSecondary"
              component="div"
              align="right"
              className={classes.fields}
            >
              <Box fontSize={14} component="span" lineHeight={1}>
                {steps?.details?.idp_endpoint || '---'}
              </Box>
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={3}
            className={classes.providerInfo}
          >
            <Typography color="textPrimary" component="div" align="left">
              <Box fontSize={16} component="h6" lineHeight={1}>
                {i18n.t('authentication.wizard.domains')}:
              </Box>
            </Typography>
            <Typography
              color="textSecondary"
              component="div"
              align="right"
              className={classes.fields}
            >
              <Box fontSize={14} component="span" lineHeight={1}>
                {steps?.details?.domains?.join(', ') || '---'}
              </Box>
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={3}
            className={classes.providerInfo}
          >
            <Typography color="textPrimary" component="div" align="left">
              <Box fontSize={16} component="h6" lineHeight={1}>
                {i18n.t('authentication.wizard.certificate')}:
              </Box>
            </Typography>
            <Typography
              color="textSecondary"
              component="div"
              align="right"
              className={classes.fields}
            >
              <Box
                fontSize={14}
                component="span"
                lineHeight={1}
                style={{
                  color: steps?.details?.has_certificate ? '#61C365' : 'inherit',
                  fontWeight: steps?.details?.has_certificate ? 600 : 'inherit',
                }}
              >
                {steps?.details?.has_certificate ? i18n.t('authentication.details.attached') : '--'}
              </Box>
            </Typography>
          </Box>
          {steps?.details?.has_certificate && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mt={3}
              className={classes.providerInfo}
            >
              <Typography color="textPrimary" component="div" align="left">
                <Box fontSize={16} component="h6" lineHeight={1} alignItems={'top'} display="flex">
                  {i18n.t('common.status')}:
                  <Tooltip
                    title={i18n.t('authentication.wizard.tooltip.status')}
                    className={classes.statusTooltip}
                  >
                    <InfoIcon color="primary" />
                  </Tooltip>
                </Box>
              </Typography>
              <Typography
                color="textSecondary"
                component="div"
                align="right"
                className={classes.fields}
              >
                <Box
                  fontSize={14}
                  component="span"
                  lineHeight={1}
                  style={{
                    color: steps?.details?.enabled ? '#61C365' : '#FA5252',
                    fontWeight: 600,
                  }}
                >
                  {steps?.details?.enabled ? i18n.t('common.active') : i18n.t('common.inactive')}
                </Box>
              </Typography>
            </Box>
          )}
          <Box display="flex" justifyContent="right" mt={5}>
            <Button
              color="primary"
              variant="contained"
              style={{
                fontWeight: 500,
              }}
              data-cy="edit-btn"
              onClick={() => onEditForm(enableForm)}
              className={classes.editButton}
            >
              {i18n.t('wizard.edit_form')}
            </Button>
          </Box>
        </Box>
      )}
    </Can>
  );
};

SsoTab.propTypes = {
  classes: PropTypes.shape({
    textContainer: PropTypes.string,
    description: PropTypes.string,
    stepperContainer: PropTypes.string,
    customRootClass: PropTypes.string,
    nextButton: PropTypes.string,
    textContainerDetails: PropTypes.string,
    providerInfo: PropTypes.string,
    fields: PropTypes.string,
    editButton: PropTypes.string,
    statusTooltip: PropTypes.string,
    note: PropTypes.string,
  }).isRequired,
};

export default SsoTab;
